import {
  createDarkTheme,
  createLightTheme,
  teamsDarkTheme,
  teamsLightTheme,
  webDarkTheme,
  webLightTheme,
  type BrandVariants,
  type Theme,
} from '@fluentui/react-components';
import {
  ACTIVE_THEME,
  APP_THEMES,
  TENANT_THEME_KEY,
  THEME_OPTIONS,
  buildMokoTheme as buildTokenTheme,
  themeConfigFromOtherInfo,
  type BaseFluentTheme,
  type MokoThemeConfig,
  type ThemeComponentConfig,
} from '@moko/themes';
import type { ColorScheme } from '@moko/utils';

export {
  ACTIVE_THEME,
  APP_THEMES,
  TENANT_THEME_KEY,
  THEME_OPTIONS,
  themeConfigFromOtherInfo,
  type MokoThemeConfig,
};

const fluentBases: Record<ColorScheme, Record<BaseFluentTheme, Theme>> = {
  light: {
    web: webLightTheme,
    teams: teamsLightTheme,
  },
  dark: {
    web: webDarkTheme,
    teams: teamsDarkTheme,
  },
};

export const buildMokoTheme = (
  config: MokoThemeConfig,
  colorScheme: ColorScheme = 'light'
): Theme => {
  const base = fluentBases[colorScheme][config.baseFluentTheme ?? 'web'];
  const tokens = buildTokenTheme(config);
  const createBrandTheme =
    colorScheme === 'dark' ? createDarkTheme : createLightTheme;
  const brandTheme = config.brand
    ? createBrandTheme(config.brand as BrandVariants)
    : undefined;

  return {
    ...base,
    ...brandTheme,
    ...(config.font?.cssStack ? { fontFamilyBase: config.font.cssStack } : {}),
    ...(colorScheme === 'light'
      ? { colorNeutralBackground1: tokens.colors.backgroundPrimary }
      : {}),
    ...(config.components?.control?.borderRadius
      ? { borderRadiusMedium: config.components.control.borderRadius }
      : {}),
  };
};

type GlobImporter = () => Promise<unknown>;

const fontModules = import.meta.glob([
  '../../node_modules/@fontsource-variable/*/index.css',
  '../../node_modules/@fontsource/*/[0-9]00.css',
]) as Record<string, GlobImporter>;

const resolveFontLoader = (
  specifier: string
): (() => Promise<unknown>) | undefined => {
  const suffix = specifier.replace(/^@fontsource/, '');
  const match = Object.keys(fontModules).find((path) =>
    path.includes(`@fontsource${suffix}`)
  );
  return match ? fontModules[match] : undefined;
};

export const loadThemeFonts = (fontImports?: string[]): Promise<unknown> =>
  Promise.all(
    (fontImports ?? []).map((specifier) => resolveFontLoader(specifier)?.())
  );

const STYLE_ID = 'moko-theme-css';

const kebabCase = (value: string): string =>
  value.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

const themeVarName = (group: string, key: string): string =>
  `--moko-${kebabCase(group)}-${kebabCase(key)}`;

export const themeVar = (
  group: string,
  key: string,
  fallback: string
): string => `var(${themeVarName(group, key)}, ${fallback})`;

export const componentCssVariables = (
  components: ThemeComponentConfig | undefined
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(components ?? {})
      .filter(([group]) => group !== 'summaryCards')
      .flatMap(([group, values]) =>
        Object.entries(values as Record<string, string | undefined>).flatMap(
          ([key, value]) =>
            value ? [[themeVarName(group, key), value] as const] : []
        )
      )
  );

export const buildThemeCss = (
  config: MokoThemeConfig,
  theme: Theme,
  colorScheme: ColorScheme = 'light'
): string => {
  const fontFamily = config.font?.cssStack;

  const fontRule = fontFamily
    ? `
    :root, body, [class*='fui-FluentProvider'] {
      font-family: ${fontFamily};
    }
    button, input, select, textarea {
      font-family: inherit;
    }
  `
    : '';

  const componentRules = Object.entries(
    colorScheme === 'light' ? componentCssVariables(config.components) : {}
  )
    .map(([name, value]) => `${name}: ${value};`)
    .join('\n      ');

  return `
    :root {
      --moko-brand: ${theme.colorBrandForeground1};
      --moko-brand-strong: ${theme.colorBrandBackground};
      --moko-brand-light: ${theme.colorBrandBackground2};
      --moko-background: ${theme.colorNeutralBackground1};
      --moko-summary-card-border-radius: ${
        config.components?.summaryCards?.cardBorderRadius ?? '4px'
      };
      ${
        config.components?.summaryCards?.cardBorderColor
          ? `--moko-summary-card-border-color: ${config.components.summaryCards.cardBorderColor};`
          : ''
      }
      --moko-summary-card-icon-border-radius: ${
        config.components?.summaryCards?.iconBorderRadius ??
        config.components?.summaryCards?.cardBorderRadius ??
        '4px'
      };
      ${componentRules}
    }
    html, body {
      background-color: ${theme.colorNeutralBackground1};
    }
    ${fontRule}
  `;
};

export const applyThemeCss = (
  config: MokoThemeConfig,
  theme: Theme,
  colorScheme: ColorScheme = 'light'
): void => {
  if (typeof document === 'undefined') return;

  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }

  const next = buildThemeCss(config, theme, colorScheme);
  if (style.textContent === next) return;
  style.textContent = next;
};
